/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useMemo } from "react";
import { BEM, useTranslate, useUuid } from "@adapptio/lib-components";
import { FieldWrapper } from "../FieldWrapper";
import {
	Icon,
	InputAdornment,
	InputButton,
	ISelectOption_Item,
	isSelectOptionItem,
	isSelectOptionSeparator,
	ListItemIcon,
	ListItemText,
	ListSubheader,
	MenuItem,
	Select,
	TSelectOption
} from "../../core";
import { IFieldValidationError } from "../FieldTypes";
import { useDeepCompareCallback } from "use-deep-compare";
import { FieldStatus } from "../FieldStatus";
import { isFunction } from "@adapptio/lib-shared";

export interface IFieldSelectProps {
	id?: string;
	label?: string;
	value: string;
	options: Array<TSelectOption>;
	placeholder?: string;
	readOnly?: boolean;
	allowCustomInput?: boolean;
	validationErrors?: IFieldValidationError[];
	className?: string;
	onChange?: (value: string) => void;
	grow?: boolean;
	clearable?: boolean;
}

const FieldSelect: React.FC<IFieldSelectProps> = ({
	id: propId,
	value,
	placeholder,
	readOnly,
	options,
	allowCustomInput,
	onChange,
	validationErrors,
	className,
	grow,
	clearable
}) => {
	const baseClass = BEM("edt-field-select");
	const t = useTranslate();
	const uuId = useUuid();
	const id = propId || uuId;

	const adornment = validationErrors?.length > 0 || clearable;

	const selectedOptionIndex = useMemo(
		() =>
			value ? options.findIndex((option) => isSelectOptionItem(option) && option.value === value) : -1,
		[ value ]
	);

	const getOptionId = useCallback(
		(option: ISelectOption_Item) => `option-${id}-${option.id || option.value}`,
		[ id ]
	);

	const selectedOption: ISelectOption_Item =
		selectedOptionIndex > -1 && isSelectOptionItem(options[selectedOptionIndex])
			? (options[selectedOptionIndex] as ISelectOption_Item)
			: null;

	const filterOptions = useDeepCompareCallback<(value: string) => TSelectOption[]>(
		(value) => {
			const filtered = options.reduce((filtered, option, index) => {
				if (
					isSelectOptionSeparator(option) &&
					!isSelectOptionSeparator(filtered[filtered.length - 1]) &&
					index !== options.length - 1
				) {
					return [ ...filtered, option ];
				}

				if (isSelectOptionItem(option)) {
					return [ ...filtered, option ];
				}

				return filtered;
			}, []);

			while (isSelectOptionSeparator(filtered[0])) {
				filtered.shift();
			}

			while (isSelectOptionSeparator(filtered[filtered.length - 1])) {
				filtered.pop();
			}

			return filtered;
		},
		[ options ]
	);

	const handleClearClick = useCallback(() => {
		if (isFunction(onChange)) {
			onChange(null);
		}
	}, [ onChange ]);

	const filteredOptions = useMemo(
		() => (!selectedOption && value ? filterOptions(value) : options),
		[ options, value, selectedOption, filterOptions ]
	);

	return (
		<FieldWrapper type="select" className={className} grow={grow}>
			<Select
				className={baseClass.toClassName()}
				value={value || ""}
				readOnly={readOnly}
				id={id}
				allowCustomInput={allowCustomInput}
				placeholder={placeholder}
				onChange={onChange}
				displayedValue={selectedOption?.label || selectedOption?.value || value || ""}
				displayedIcon={selectedOption?.icon || null}
				endAdornment={
					adornment && (
						<InputAdornment>
							{validationErrors?.length > 0 && <FieldStatus errors={validationErrors} />}
							{clearable && (
								<InputButton
									className={baseClass.element("editor-clear").toClassName()}
									onClick={handleClearClick}
									tabIndex={-1}
									disabled={!value}
								>
									<Icon
										className={baseClass.element("editor-clear-icon").toClassName()}
										name="mdi/close"
									/>
								</InputButton>
							)}
						</InputAdornment>
					)
				}
			>
				{filteredOptions?.length > 0 ? (
					filteredOptions.map((option, index) =>
						isSelectOptionItem(option) ? (
							<MenuItem key={getOptionId(option)} id={getOptionId(option)} value={option.value}>
								{option.icon && (
									<ListItemIcon>
										<Icon name={option.icon} />
									</ListItemIcon>
								)}
								<ListItemText
									align="left"
									primary={t("editor", option.label) || option.value}
									secondary={t("editor", option.description)}
								/>
							</MenuItem>
						) : (
							<ListSubheader key={index} />
						)
					)
				) : (
					<MenuItem disabled className={baseClass.element("empty").toClassName()}>
						No items found
					</MenuItem>
				)}
			</Select>
		</FieldWrapper>
	);
};

const FieldSelectMemo = React.memo(FieldSelect);

export { FieldSelectMemo as FieldSelect };
